import React from "react"
import { GetStaticPaths, GetStaticProps } from "next"
import { Container, Grid, Box } from "@mui/material"

import { makeStyles } from "makeStyles"

import LazyLoadedImage from "components/LazyLoadedImage"
import SEO from "@secureo/common/components/SEO"
import Link from "components/i18n/Link"
import BreadCrumbs, { BreadCrumb } from "components/BreadCrumbs"
import { anonymousAccessTokenCache } from "@secureo/common/utils/commerceTools/token/accessTokenCache"
import fetchBrandCategories from "@secureo/common/utils/commerceTools/graphql/queries/fetchBrandCategories"

import { Category } from "@secureo/common/typings/Category"
import { supportedValidLocales } from "@secureo/common/utils/i18n/supportedLocalesAndCurrencyCodes"

import HerstellerGrafik from "svg/contentPages/hersteller.svg"

const useStyles = makeStyles()({
	responsiveItem: {
		width: "49.5%",
		position: "relative",
		marginBottom: 50,
		"@media only screen and (max-width: 960px)": {
			width: "100%",
		},
	},
	item: {
		"@media screen and (max-width: 960px)": {
			borderRight: "none",
		},
		"@media screen and (min-width: 960px)": {
			"&:nth-child(2n + 1)": {
				borderRight: "1px solid #e1e6eb",
			},
		},
		"&:nth-child(4n)": {
			"& .background-4th-childs": {
				position: "absolute",
				top: "0px",
				right: "0px",
				height: "210px",
				width: "95%",
				background: "#e7eff6",
			},
		},
		"&:nth-child(4n + 1)": {
			/* selects 1, 5, 9, 13 .. */
			"& .background-odd-child": {
				position: "absolute",
				top: "0px",
				left: "0px",
				height: "210px",
				width: "95%",
				background: "#e7eff6",
			},
		},
		"&:nth-child(odd)": {
			"& .content": {
				maxWidth: "610px",
				float: "right",
			},
		},
		"&:nth-child(even)": {
			"& .content": {
				maxWidth: "610px",
				float: "left",
			},
		},
	},
})

const BrandsOverviewPage = ({ locale, brandCategories }: Props) => {
	const { classes } = useStyles()
	const asPath = `/${locale}/brands`

	const currentPath: BreadCrumb = {
		label: "Marken",
		href: `/brands`,
	}

	return (
		<>
			<SEO asPath={asPath} appendLocalizedShopNameWithPipe title="Markenübersicht" />
			<BreadCrumbs currentPath={currentPath} locale={locale} />
			<Box pb={5} mb={5}>
				<Container>
					<Box mt={5} mb={10}>
						<Grid container spacing={3}>
							<Grid item xs={12} md={5}>
								<div style={{ maxWidth: 450 }}>
									<div
										style={{
											color: "#4691CE",
											textTransform: "uppercase",
											fontSize: 13,
										}}
									>
										<strong>Höchste Qualität</strong>
									</div>
									<Box mb={2}>
										<h1 style={{ margin: 0 }}>Unsere Hersteller</h1>
									</Box>
									<p style={{ color: "#505e5f" }}>
										Hier finden Sie eine Übersicht unserer Hersteller. Die
										meisten unserer Produkte werden in Österreich entwickelt und
										produziert. Um höchste Qualitätsansprüche zu erfüllen,
										arbeiten wir nur mit den qualifiziertesten Herstellern in
										dieser Branche zusammen.
									</p>
								</div>
							</Grid>
							<Grid item xs={12} md={7} style={{ textAlign: "center" }}>
								<HerstellerGrafik
									style={{
										width: "100%",
										maxWidth: 700,
									}}
								/>
							</Grid>
						</Grid>
					</Box>
				</Container>
				<div style={{ display: "flex", flexFlow: "row wrap" }}>
					{brandCategories.map((brandCategory) => {
						const { name, slug, icon, shortDescription, categoryImg } = brandCategory

						const cutDescription = (shortDescription = "") =>
							shortDescription.length > 220
								? `${shortDescription.substring(0, 220)} ...`
								: shortDescription

						return (
							<div
								key={brandCategory.id}
								className={`${classes.responsiveItem} ${classes.item}`}
							>
								<div className="background-odd-child" />
								<div className="background-4th-childs" />
								<Box p={3} className="content">
									<Link href={`/brands/${slug}`}>
										<div
											style={{
												position: "relative",
												textAlign: "center",
												mixBlendMode: "multiply",
											}}
										>
											<LazyLoadedImage
												alt={name}
												title={name}
												src={categoryImg}
												style={{
													height: 176,
													width: 200,
													margin: "auto",
													marginTop: -5,
												}}
											/>

											<img
												src={icon}
												alt={name}
												style={{
													height: "100px",
													maxHeight: 60,
													width: "100px",
													objectFit: "contain",
													position: "absolute",
													top: 0,
													right: 0,
												}}
											/>
										</div>
									</Link>
									<Box my={2}>
										<strong>{name}</strong>
									</Box>
									<Box mb={2}>
										<div
											dangerouslySetInnerHTML={{
												__html: cutDescription(shortDescription),
											}}
										/>
									</Box>
									<Link href={`/brands/${slug}`}>
										Mehr Informationen zu {name}
									</Link>
								</Box>
							</div>
						)
					})}
				</div>
			</Box>
		</>
	)
}

interface Props {
	brandCategories: Category[]
	locale: string
}

export const getStaticPaths: GetStaticPaths = async () => {
	const paths = supportedValidLocales.map((locale) => {
		return { params: { locale } }
	})

	return {
		paths,
		fallback: false,
	}
}

export const getStaticProps: GetStaticProps = async (context) => {
	const accessToken = await anonymousAccessTokenCache()
	const locale = context.params.locale as string

	const brandCategories = await fetchBrandCategories(accessToken, locale, true)

	const props: Props = {
		brandCategories,
		locale,
	}

	return { props }
}

export default BrandsOverviewPage
